// @flow

import type {
  EventTarget,
  EasyCompObject,
  EasyCompMap,
  EventSource,
  JsEnv
} from '../type'

import * as React from 'react'
import { convertMapToObject, getIteratorIndexId } from './util'
import { updateActionNum } from '../redux/action-num'
import * as analysis from 'frame/core/analysis'
import { getPageInfo } from '../pageInfo'

export type Props = {
  easyCompMap: EasyCompMap,
  iteratorIndex?: string,
  customProps?: any
};

export const allCompMap: { [easyCompId: string]: React.Component<*> } = {}

export default class EasyCompComponent extends React.PureComponent<Props> {
  registerRef (
    component: ?React.Component<*>,
    easyCompId: string,
    iteratorIndex?: string
  ): mixed {
    const compId = easyCompId + getIteratorIndexId(iteratorIndex)
    if (component) {
      allCompMap[compId] = component
    } else {
      delete allCompMap[compId]
    }
  }

  render () {
    const { easyCompMap, customProps, iteratorIndex } = this.props
    if (!easyCompMap) {
      return null
    }
    const easyComp: EasyCompObject = convertMapToObject(easyCompMap)

    let children = easyComp.children
    const { type } = easyComp
    if (
      type !== 'iterator' &&
      type !== 'customProps' &&
      type !== 'customPropsConfig' &&
      type !== 'complex'
    ) {
      children =
        easyComp.children &&
        easyComp.children.map((easyCompMap: EasyCompMap) => {
          return (
            <EasyCompComponent
              key={easyCompMap.get('id')}
              easyCompMap={easyCompMap}
              customProps={customProps}
              iteratorIndex={iteratorIndex}
            />
          )
        })
    }
    return (
      <easyComp.comp
        {...easyComp.props}
        ref={ref => this.registerRef(ref, easyComp.id, iteratorIndex)}
        id={easyComp.id + '_comp' + getIteratorIndexId(iteratorIndex)}
        compId={easyComp.id}
        customProps={customProps}
        callActions={(eventName: string, dynamicData: any, jsEnv?: JsEnv) => {
          let events =
            (easyComp.props &&
              easyComp.props.events &&
              easyComp.props.events[eventName]) ||
            []
          callActions({
            type,
            events,
            compId: easyComp.id,
            eventName,
            dynamicData,
            iteratorIndex,
            jsEnv
          })
        }}
        children={children}
      />
    )
  }
}

export function callComponentAction (compId: string, actionName: string) {
  callAction({
    event: {
      eventName: 'notUse',
      targetAction: actionName,
      targetCompId: compId,
      iteratorIndex: '0,0,0,0,0,0'
    }
  })
}

function callAction ({
  event,
  jsEnv,
  defaultValue
}: {
  event: EventTarget,
  jsEnv?: JsEnv,
  defaultValue?: ?any
}) {
  const {
    targetCompId,
    targetAction,
    data,
    condition,
    iteratorIndex,
    dataCode
  } = event
  let { dynamicData = {} } = event
  let initDynamicData = dynamicData
  if (jsEnv && dataCode) {
    dynamicData = jsEnv.runCodeWithAdditionalCtx(dataCode, dynamicData)
  } else if (defaultValue) {
    dynamicData = defaultValue
  }

  if (condition && jsEnv) {
    let conditionContext = dynamicData
    if (dynamicData !== initDynamicData) {
      if (
        typeof initDynamicData !== 'object' ||
        Array.isArray(initDynamicData)
      ) {
        initDynamicData = { value: initDynamicData }
      }
      conditionContext = {
        ...initDynamicData,
        ...dynamicData
      }
    }
    if (!jsEnv.runCodeWithAdditionalCtx(condition, conditionContext)) {
      return
    }
  }
  let component: ?React.Component<*> = allCompMap[targetCompId]
  if (!component && iteratorIndex != null) {
    const indexes = iteratorIndex.split(',')
    let key = ''
    indexes.find(item => {
      key = key + item
      component = allCompMap[targetCompId + key]
      return component
    })
  }
  const actionName =
    'action' + targetAction.charAt(0).toUpperCase() + targetAction.substr(1)
  const forwardFnName = 'actionForward'
  let forwardFn
  let isExec = false
  while (component) {
    // $FlowFixMe
    const actionFn = component[actionName]
    // $FlowFixMe
    forwardFn = component[forwardFnName]
    if (typeof actionFn === 'function') {
      actionFn(data, dynamicData)
      isExec = true
      return
    }
    component =
      // $FlowFixMe
      component.wrappedComponent ||
      // $FlowFixMe
      (component.getWrappedInstance && component.getWrappedInstance())
  }
  if (!isExec && forwardFn && typeof forwardFn === 'function') {
    forwardFn(targetAction, data, dynamicData)
  }
}

export function customRender (
  children?: Array<EasyCompMap>,
  props: {
    customProps: any
  },
  iteratorIndex?: Array<number>
) {
  if (children) {
    let index = iteratorIndex ? iteratorIndex.join(',') : ''
    return children.map((easyCompMap: EasyCompMap) => {
      return (
        <EasyCompComponent
          {...props}
          key={easyCompMap.get('id') + index}
          iteratorIndex={index}
          easyCompMap={easyCompMap}
        />
      )
    })
  }
}

function callActions ({
  compId,
  events,
  eventName,
  dynamicData,
  iteratorIndex,
  jsEnv,
  type
}: {
  type?: string,
  compId: string,
  events: EventTarget[] | EventSource,
  eventName: string,
  dynamicData: any,
  iteratorIndex?: string,
  jsEnv?: JsEnv
}) {
  const eventSource: EventSource = Array.isArray(events)
    ? {
      targets: events
    }
    : events

  const { targets, dataCode } = eventSource

  let specialCode = getPageInfo().code
  if (!targets || targets.length === 0) {
    return
  }
  if (!type || type !== 'Interval') {
    analysis.trackEventValue(
      `${specialCode}$-$${compId}${
        iteratorIndex == null ? '' : '$' + iteratorIndex
      }`,
      { eventName }
    )
  }

  let defaultValue
  if (jsEnv && dataCode) {
    defaultValue = jsEnv.runCodeWithAdditionalCtx(dataCode, dynamicData || {})
  }

  targets.forEach((event: EventTarget) => {
    callAction({
      event: { ...event, dynamicData, iteratorIndex },
      jsEnv,
      defaultValue
    })
  })
  // 更新 redux state，以便刷新预览选择框
  setTimeout(updateActionNum, 100)
}

export const originCallActions = callActions



// WEBPACK FOOTER //
// ./src/projects/easy/public/Comps/EasyCompComponent.js